import { flexRender, type ColumnDef, type Table as TanStackTable } from "@tanstack/react-table";

import Loading from "@/components/Loading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getColumnSortAria } from "@/components/ui/sortable-column-header";
import { cn } from "@/lib/utils";

export interface ColumnMeta {
  cellClassName?: string;
  headerClassName?: string;
}

export const getColumnMeta = <TData,>(columnDef: ColumnDef<TData, unknown>) =>
  columnDef.meta as ColumnMeta | undefined;

interface DataTableProps<TData> {
  emptyMessage: string;
  isLoading: boolean;
  table: TanStackTable<TData>;
}

/**
 * Renders a TanStack table instance as a shadcn `<Table>`, with a loading row and an
 * empty-state row. Per-column widths and alignment come from each column's
 * `meta: { cellClassName, headerClassName }`.
 */
export const DataTable = <TData,>({ emptyMessage, isLoading, table }: DataTableProps<TData>) => {
  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map(headerGroup => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <TableHead
                key={header.id}
                className={getColumnMeta(header.column.columnDef)?.headerClassName}
                aria-sort={getColumnSortAria(header.column)}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={columnCount}>
              <Loading isVisible small />
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && rows.length
          ? rows.map(row => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <TableCell
                    key={cell.id}
                    className={cn(getColumnMeta(cell.column.columnDef)?.cellClassName, "p-3")}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          : null}
      </TableBody>
    </Table>
  );
};
